import apiFetch from '@wordpress/api-fetch';

export const FETCH_COURSE_SETTINGS_SUCCESS = 'FETCH_COURSE_SETTINGS_SUCCESS';
export const FETCH_USER_SETTINGS_SUCCESS = 'FETCH_USER_SETTINGS_SUCCESS';
export const UPDATE_SETTINGS = 'UPDATE_SETTINGS';
export const SAVE_SETTINGS_SUCCESS = 'SAVE_SETTINGS_SUCCESS';
export const SET_USER_COURSES = 'SET_USER_COURSES';

const fetchCourseSettingsSuccess = (settings) => ({
  type: FETCH_COURSE_SETTINGS_SUCCESS,
  payload: settings,
});

const fetchUserSettingsSuccess = (settings) => ({
  type: FETCH_USER_SETTINGS_SUCCESS,
  payload: settings,
});

const updateSettings = (settings) => ({
  type: UPDATE_SETTINGS,
  payload: settings,
});

const saveSettingsSuccess = () => ({
  type: SAVE_SETTINGS_SUCCESS,
});

const setUserCourses = (courses) => ({
  type: SET_USER_COURSES,
  payload: courses,
});

export const fetchCourseSettings = (courseID) => async (dispatch) => {
  try {
    const response = await apiFetch({
      path: `/units/v1/learndash_units_settings/${courseID}`,
      method: 'GET',
    });

    if (response.length === 0) {
      const initialSettings = await getInitialCourseSettings(courseID);
      dispatch(updateSettings(initialSettings));
      dispatch(fetchCourseSettingsSuccess(initialSettings));
    } else {
      const parsedSettings = JSON.parse(response.settings);
      dispatch(updateSettings(parsedSettings));
      dispatch(fetchCourseSettingsSuccess(parsedSettings));
    }
  } catch (error) {
    console.error('Failed to fetch course settings:', error);
  }
};

export const fetchUserSettings = (userID) => async (dispatch) => {
  try {
    const response = await apiFetch({
      path: `/units/v1/enrolled-courses/${userID}`,
      method: 'GET',
    });
    dispatch(setUserCourses(response));
    dispatch(fetchUserSettingsSuccess(response));
    await setUserCoursesSettings(dispatch, userID, response);
  } catch (error) {
    console.error('Failed to fetch user settings:', error);
  }
};

export const saveCourseSettings = (courseId, settings) => async (dispatch) => {
  try {
    await apiFetch({
      path: `/units/v1/learndash_units_settings`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        post_id: courseId,
        settings: JSON.stringify(settings),
      }),
    });
    dispatch(saveSettingsSuccess());
  } catch (error) {
    console.error('Failed to save settings:', error);
  }
};

export const saveUserSettings = (userId, settings) => async (dispatch) => {
  try {
    await apiFetch({
      path: `/units/v1/user_settings`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        settings: JSON.stringify(settings), // No need to stringify here
      }),
    });
    dispatch(saveSettingsSuccess());
  } catch (error) {
    console.error('Failed to save user settings:', error);
  }
};

export const updateSettingsAction = (settings) => (dispatch) => {
  dispatch(updateSettings(settings));
};

const setUserCoursesSettings = async (dispatch, userID, courses) => {
  try {
    const metaResponse = await apiFetch({
      path: `/units/v1/user-meta/${userID}/course_settings`,
      method: 'GET',
    });

    if (!metaResponse || (metaResponse.error && metaResponse.error === "the settings are not set")) {
      await initializeCourseSettings(dispatch, courses);
    } else {
      dispatch(updateSettings(JSON.parse(metaResponse[0])));
    }
  } catch (metaError) {
    console.error('Error fetching user meta:', metaError);
  }
};

const initializeCourseSettings = async (dispatch, courses) => {
  const allSettings = [];

  for (const course of courses) {
    const settings = await getInitialCourseSettings(course.ID, course.name);
    if (settings) {
      allSettings.push(settings);
    } else {
      console.error('Failed to get settings for course:', course);
    }
  }

  if (allSettings.length > 0) {
    dispatch(updateSettings(allSettings));
  } else {
    console.error('No settings were retrieved for any courses.');
  }
};

const getInitialCourseSettings = async (postId, courseName = null) => {
  if (!postId) {
    console.error('ID is undefined or null');
    return undefined;
  }

  try {
    const lessonsData = await apiFetch({ path: `ldlms/v2/sfwd-lessons?course=${postId}` });
    const stepsData = await apiFetch({ path: `ldlms/v2/sfwd-courses/${postId}/steps` });
    const solutionTypesData = await apiFetch({ path: `/units/v1/course-meta/${postId}` });
    
    const unitsSettings = stepsData.sections.map(section => ({
      title: section.post_title,
      id: section.ID,
      lessons: section.steps.map(lesson => ({
        id: lesson,
        title: findLessonTitleById(lessonsData, lesson),
        options: solutionTypesData
      })),
      options: solutionTypesData
    }));

    const courseSettings = {
      id: postId,
      name: courseName || null,
      options: solutionTypesData,
      units: unitsSettings,
    };

    return courseSettings;
  } catch (error) {
    console.error('Failed to fetch initial course settings:', error);
    return undefined;
  }
};

const findLessonTitleById = (lessons, lessonId) => {
  const lesson = lessons.find(lesson => lesson.id === lessonId);
  return lesson ? lesson.title.rendered : 'Lesson not found';
};

export const fetchSolutionTypesForCourse = (courseID) => async () => {
  try {
    const response = await apiFetch({
      path: `/units/v1/course-meta/${courseID}`,
      method: 'GET',
    });

    if (response && Array.isArray(response)) {
      return response;
    } else {
      console.error('No associated solution types found for the course or meta not set.');
      return [];
    }
  } catch (error) {
    console.error('Failed to fetch solution types for the course:', error);
    return [];
  }
};

